import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Defines the shape of the translation object (loaded dynamically)
interface Translations {
    nav: Record<string, string>;
    aw: Record<string, string>;
    about: Record<string, string>;
    catalog: Record<string, string>;
    cards: Record<string, string>;
}

// Initial state
const initialTranslations: Translations = {
    nav: {},
    aw: {},
    about: {},
    catalog: {},
    cards: {}
};

type Language = 'es' | 'en';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [language, setLanguage] = useState<Language>('es');
    const [t, setT] = useState<Translations>(initialTranslations);

    useEffect(() => {
        const fetchTranslations = async () => {
            try {
                const response = await fetch(`/locales/${language}/translation.json`);
                if (response.ok) {
                    const data = await response.json();
                    setT(data);
                } else {
                    console.error(`Failed to load translations for ${language}`);
                }
            } catch (error) {
                console.error('Error loading translations:', error);
            }
        };

        fetchTranslations();
    }, [language]);

    const value = {
        language,
        setLanguage,
        t
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
